/**
 * accountSettings — "Save Changes" saves, and "Change password" sends the email.
 *
 * WHAT THEY DID. Both controls on the account form were pure toasts:
 *   #acct-save  ->  toast("Saved · your matches just got sharper")   app.js:5175
 *   #acct-pass  ->  toast("Password reset email sent")               app.js:5174
 * Neither made a call. The second is the worse of the two and the same class as
 * #verify-resend: it fails OUTSIDE the app, so nothing on screen contradicts it
 * and the person waiting is the one who cannot get back in.
 *
 * ## What saves, and what deliberately does not
 *
 * Six controls map onto real fields (owner ruling, 2026-08-28):
 *
 *   Categories of Interest   ACCOUNT.cats      -> categoryInterests
 *   Style Vibe               ACCOUNT.vibes     -> styleVibes
 *   Clothing Size            ACCOUNT.clothing  -> clothingSize
 *   Shoe Size                #acct-shoe        -> shoeSize
 *   Username / Email         #acct-handle etc  -> username / email
 *   Shipping address         five inputs       -> PUT /me/shipping-address
 *
 * Two do NOT, and are being removed rather than silently dropped:
 *
 * 📌 Shopping Preference has no column. The nearest name, `exchangePrefs`, is
 *    {acceptedOfferTypes, preferredCategories, willingToShip} — what offers you
 *    accept, not what you came here to do. Its own hint promises "We'll filter
 *    out listings that don't match your preference", so a column with nothing
 *    reading it would be a chip that saves and changes nothing.
 * 📌 Age Range cannot round-trip. The schema holds `birthYear` AND a full
 *    `dateOfBirth`, which is the source of truth for the minimum-age gate, so age is
 *    already collected more precisely at signup. A range of years cannot become
 *    a date, and re-asking would make the data worse.
 *
 * Until Blake drops those two chips they still move ACCOUNT locally and are
 * still lost on reload. That is a smaller lie than saving them nowhere while
 * saying "Saved", and it is carried openly rather than papered over.
 *
 * ## 🪤 IDENTITY FIELDS GO ONLY WHEN EDITED
 *
 * Username and email each 409 on conflict (users.ts:344, :360), and one PATCH
 * carries every field — so submitting an unchanged-but-taken username would
 * fail the whole save, losing the sizes and categories the person actually
 * came to change. `input.defaultValue` is the value Blake rendered into the
 * attribute and `input.value` is what is on screen now, so the diff is native
 * and needs no extra bridge accessor.
 *
 * ## 🔴 TWO REQUESTS MEANS PARTIAL SUCCESS IS POSSIBLE
 *
 * The profile and the address are different endpoints and either can fail
 * alone. "Saved" over a half-saved form is exactly the claim this lane exists
 * to remove, so each outcome gets its own sentence and a failure names the half
 * that failed.
 */

/** His two controls (app.js:2603 and the row above it). */
export const SAVE_SELECTOR = "#acct-save";
export const PASSWORD_SELECTOR = "#acct-pass";

export const SAVED_MESSAGE = "Saved";
export const SAVED_PROFILE_ONLY_MESSAGE = "Profile saved — your address needs all five fields";
export const ADDRESS_FAILED_MESSAGE = "Profile saved, but your address did not";
export const PROFILE_FAILED_MESSAGE = "Nothing was saved — please try again";
export const SIGNIN_MESSAGE = "Sign in to save your settings";

/**
 * 🔴 ROUTED, NOT SENT — and the first version got this wrong in production.
 *
 * It fired POST /api/auth/forgot-password straight from this form and toasted
 * that the email was sent. The email really did arrive. It carries a 6-DIGIT
 * CODE, and the marketplace has nowhere to type one, so the owner was left
 * holding a code with no next step (reported 2026-08-28, on prod).
 *
 * Sending was never the hard part. The flow is three steps — request the code,
 * enter the code, choose a new password — and POST /api/auth/reset-password
 * (auth.ts:616) requires { email, code, newPassword } together. Firing step one
 * in isolation strands the person between step one and two.
 *
 * pages/forgot-password.tsx already implements all three (its own `step` state
 * machine, :39) and calls both endpoints correctly. So this hands off to the
 * real flow rather than half-performing it here — the same call accountActions
 * made for account deletion, and for the same reason: the correct client has a
 * step this markup cannot provide.
 *
 * 📌 It no longer sends the email itself. The destination sends it, and the
 * person is standing in front of the box the code goes into when it arrives.
 * Sending here as well would deliver two codes and invalidate the first.
 */
export const RESET_FLOW_URL = "/forgot-password";

/** Says what is about to happen, because a hard navigation is about to happen. */
export const RESET_HANDOFF_MESSAGE = "Opening password reset";

interface SettingsWindow extends Window {
  /** Bridge: the chip state Blake's own handlers mutate. */
  __gbzAccountDraft?: () => {
    email: string;
    cats: string[];
    vibes: string[];
    clothing: string;
  } | null;
  toast?: (message: string) => void;
}

function input(doc: Document, id: string): HTMLInputElement | null {
  return doc.getElementById(id) as HTMLInputElement | null;
}

/** Edited, not merely present. See the header. */
function ifEdited(el: HTMLInputElement | null): string | undefined {
  if (!el) return undefined;
  const now = el.value.trim();
  if (now === el.defaultValue.trim()) return undefined;
  return now;
}

export function installAccountSettings(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as SettingsWindow).toast?.(message);
    },
    /** Seam for tests. A HARD navigation: it leaves the bundle. */
    navigate = (url: string) => win.location.assign(url),
  }: { notify?: (m: string) => void; navigate?: (url: string) => void } = {},
): () => void {
  let busy = false;

  /** The server's sentence when it gave one, ours only when it did not. */
  async function serverError(res: Response, fallback: string): Promise<string> {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    return body.error ?? fallback;
  }

  async function save(): Promise<void> {
    const doc = win.document;
    const draft = (win as SettingsWindow).__gbzAccountDraft?.() ?? null;

    const profile: Record<string, unknown> = {};
    if (draft) {
      profile.categoryInterests = draft.cats;
      profile.styleVibes = draft.vibes;
      if (draft.clothing) profile.clothingSize = draft.clothing;
    }
    const shoe = (doc.getElementById("acct-shoe") as HTMLSelectElement | null)?.value;
    if (shoe) profile.shoeSize = shoe;

    const handle = ifEdited(input(doc, "acct-handle"));
    if (handle) profile.username = handle;
    const email = ifEdited(input(doc, "acct-email"));
    if (email) profile.email = email;

    const res = await authFetch("/api/users/me", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(profile),
    });
    if (res.status === 401) { notify(SIGNIN_MESSAGE); return; }
    if (!res.ok) {
      /* 409 is a taken username or email — the one thing the person can act on,
         and the reason identity fields are only sent when edited. */
      notify(await serverError(res, PROFILE_FAILED_MESSAGE));
      return;
    }

    /* THE ADDRESS IS ALL-OR-NOTHING, mirroring the route: it 400s unless name,
       street1, city, state and zip are ALL present (users.ts:1250). Sending a
       partial would spend a request to be told what we can already see, so a
       partial address is reported instead of submitted. */
    const ship = {
      name: input(doc, "acct-name")?.value.trim() ?? "",
      street1: input(doc, "acct-street")?.value.trim() ?? "",
      city: input(doc, "acct-city")?.value.trim() ?? "",
      state: input(doc, "acct-state")?.value.trim() ?? "",
      zip: input(doc, "acct-zip")?.value.trim() ?? "",
    };
    const complete = Object.values(ship).every((v) => v.length > 0);
    const started = Object.values(ship).some((v) => v.length > 0);

    if (!complete) {
      // An untouched address is not a failure; a half-filled one is worth saying.
      notify(started ? SAVED_PROFILE_ONLY_MESSAGE : SAVED_MESSAGE);
      return;
    }

    /* 📌 street on his form, street1 on the wire. He has one street line. */
    const addrRes = await authFetch("/api/users/me/shipping-address", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(ship),
    });
    if (!addrRes.ok) {
      /* Its 400s are specific and useful — "zip must be a 5-digit US postal
         code", "state must be a 2-letter US state abbreviation" — so they are
         quoted rather than flattened. The profile really did save, and the
         sentence says which half did not. */
      notify(await serverError(addrRes, ADDRESS_FAILED_MESSAGE));
      return;
    }
    notify(SAVED_MESSAGE);
  }

  /**
   * Hand off to the real reset flow. See RESET_FLOW_URL for why this does not
   * send the email itself.
   *
   * 🪤 A HARD NAVIGATION, not a hash change: /forgot-password is a real SPA
   * route outside the marketplace bundle, so it has to leave rather than be
   * routed to internally. Same mechanism as the account-deletion handoff.
   */
  function resetPassword(): void {
    notify(RESET_HANDOFF_MESSAGE);
    navigate(RESET_FLOW_URL);
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;

    const isSave = !!target.closest(SAVE_SELECTOR);
    const isPass = !isSave && !!target.closest(PASSWORD_SELECTOR);
    if (!isSave && !isPass) return;
    if (busy) return;

    /* The reset arm is synchronous now — it navigates rather than awaiting a
       request — so only the save path needs the in-flight guard. */
    if (!isSave) { resetPassword(); return; }

    busy = true;
    void save()
      .catch(() => notify(PROFILE_FAILED_MESSAGE))
      .finally(() => { busy = false; });
  };

  /* Capture, after the write guard: both controls are in GUARDED_WRITES and
     stay there. The guard kills his toasts — which claim a save and an email
     that never happened — and this does the real work. Not stopping
     propagation keeps the guard as the fail-safe. */
  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
